import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from "react";
import { initializeApp } from "firebase/app";
import { getAuth, signInWithCustomToken, onAuthStateChanged, signOut, User } from "firebase/auth";
import { doc, getFirestore, setDoc } from "firebase/firestore";
import { useDispatch, useSelector } from "react-redux";
import { fetchFirebaseBackendTokenThunk } from "slices/auth/thunk";
import { AppDispatch, RootState } from "store";
import apiConfig from "constants/apiConfig";
import { isFresh } from "utils/helpers";

const FIREBASE_TOKEN_STALE_TIME = apiConfig.CREATE_FIREBASE_TOKEN.staleTime;
// Initialize Firebase
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
};
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app, "chats");

// Context types
interface FirebaseContextProps {
    firebaseUser: User | null;
    loading: boolean;
    auth: typeof auth;
    db: typeof db;
    appUser: {
        name: string,
        profile_photo_url: string,
        uuid: string,
        access_type: string,
    } | null;
}
const FirebaseContext = createContext<FirebaseContextProps>({
    firebaseUser: null,
    loading: true,
    auth,
    db,
    appUser: null
});

export const useFirebase = () => useContext(FirebaseContext);

// Provider
export const FirebaseProvider = ({ children }: { children: ReactNode }) => {
    const dispatch: AppDispatch = useDispatch();
    const agencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const firebaseCustomToken = useSelector((state: RootState) => state.auth.firebase_tokens[agencyId!]);

    const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const appUser = currentUser ? {
        name: currentUser?.name,
        profile_photo_url: currentUser?.profile_photo_url,
        uuid: currentUser?.uuid,
        access_type: currentUser?.access_type,
    } : null;

    const authenticated = useRef(false);


    // Track Firebase auth state
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setFirebaseUser(user);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Sign in with custom token
   useEffect(() => {
    const tokenExpired = !isFresh(firebaseCustomToken?.generatedAt, FIREBASE_TOKEN_STALE_TIME);
    if (firebaseCustomToken && !tokenExpired && !authenticated.current && !firebaseUser) {
        authenticated.current = true;
        signInWithCustomToken(auth, firebaseCustomToken.token)
            .then(() => authenticated.current = false)
            .catch(console.error);
    } else if (tokenExpired) {
        signOut(auth).then(() => {
            authenticated.current = false;
        });
    }
}, [firebaseCustomToken, firebaseUser]);



    // Fetch backend token
    useEffect(() => {
        if(currentUser) 
            dispatch(fetchFirebaseBackendTokenThunk());
    }, [dispatch, currentUser]);

    return (
        <FirebaseContext.Provider value={{ firebaseUser, loading, auth, db, appUser }}>
            {children}
        </FirebaseContext.Provider>
    );
};
